import { Dispatch, useMemo, useState } from 'react'
import { AlertTriangle, ChevronDown, Loader2 } from 'lucide-react'
import { cn } from 'components/UI/cn'
import Checkbox from 'components/Checkbox/checkbox'
import { useMountEffect } from 'hooks/useMountEffect'
import {
  columnOptions,
  getColumnOption,
  AUTO_MATCH_PATTERNS,
} from 'features/importerv2/columnMappingShared'
import { autoMatchWithConflictResolution } from 'features/importerv2/auto-match'
import { setHeaderMapping } from 'features/importerv2/importDataTransforms'
import { ImportData } from 'features/importerv2/importer-types'
import {
  createImportPreview,
  fetchImportPreviewData,
  rePrepareImportPreview,
} from 'features/importerv2/importer-api'
import { toRowsJSON, fromRowsJSON } from 'features/importerv2/ImporterViewModel'
import { CONTACT_IMPORT_FIELD_KEYS } from '../contactImportFields'
import { pollUntilIdle } from '../pollImport'
import { WizardState, WizardAction } from '../wizardState'
import { SampleDataPreview } from './MapColumnsPreview'

// ---------------------------------------------------------------------------
// Constants derived at module level (no runtime overhead per render)
// ---------------------------------------------------------------------------

type ContactFieldKey = (typeof CONTACT_IMPORT_FIELD_KEYS)[number]

const CONTACT_FIELD_KEY_SET: ReadonlySet<string> = new Set<string>(
  CONTACT_IMPORT_FIELD_KEYS
)

/**
 * Auto-match patterns restricted to the contact-import field set.
 * Prevents auto-matching from ever proposing a non-contact field (e.g.
 * deal_sales_stage, tag_name, company_notes) when parsing a contact CSV.
 */
const FILTERED_PATTERNS: Record<string, string[]> = Object.fromEntries(
  Object.entries(AUTO_MATCH_PATTERNS).filter(([key]) =>
    CONTACT_FIELD_KEY_SET.has(key)
  )
)

/**
 * Grouped target options for the "Map to existing" dropdown, restricted to the
 * contact-import field set. Identity fields first, then Company.
 * (No CRM/deal fields are in scope for the standalone contacts importer.)
 */
const TARGET_GROUPS: { label: string; keys: ContactFieldKey[] }[] = [
  {
    label: 'Identity',
    keys: [
      'contact_first_name',
      'contact_last_name',
      'contact_full_name',
      'contact_title',
      'contact_phone',
      'contact_work_email',
      'contact_personal_email',
      'contact_generic_email',
      'contact_linkedin_url',
      'contact_label',
    ],
  },
  {
    label: 'Company',
    keys: ['company_name'],
  },
]

// CSV Column | First Bite Column | Include | (preview)
const AUTO_GRID = 'grid-cols-[minmax(0,1fr)_minmax(0,1fr)_80px_48px]'
// CSV Column | Action | Map to | (preview)
const UNMATCHED_GRID = 'grid-cols-[minmax(0,1fr)_180px_minmax(0,1fr)_48px]'

/** Field key sentinel meaning "not imported". */
const IGNORE = 'ignore'

/**
 * Stable, order-sensitive signature of a column mapping. Two mappings produce
 * the same signature iff every (original, mapped) pair matches in the same
 * order. Used to decide whether Next can reuse the existing prepared preview or
 * must re-prepare it because the mapping changed.
 */
function mappingSignature(
  headers: { original: string; mapped: string }[]
): string {
  return JSON.stringify(headers.map((h) => [h.original, h.mapped]))
}

// ---------------------------------------------------------------------------
// MapColumnsStep
// ---------------------------------------------------------------------------

interface MapColumnsStepProps {
  state: WizardState
  dispatch: Dispatch<WizardAction>
}

/**
 * Step 2 of the contact import wizard.
 *
 * Auto-matches headers once on mount, then partitions columns into two
 * sections: "Auto-Matched" (columns auto-match resolved to a real field) and
 * "Unmatched" (everything else). Auto-matched rows expose an Include checkbox;
 * unmatched rows expose a Skip / Map-to-existing action. Both sections expose a
 * sample-data preview. Excluding a column (uncheck Include, or Skip action)
 * sets that header's `mapped` to 'ignore'; the required-field gate and the
 * create-preview-on-Next logic operate on the effective (included) mappings.
 */
export function MapColumnsStep({ state, dispatch }: MapColumnsStepProps) {
  const importData = state.importData
  const [isCreating, setIsCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  /** Which column's preview popover is open (one at a time). */
  const [openPreview, setOpenPreview] = useState<string | null>(null)
  /**
   * For each auto-matched column, the field it was assigned on mount. Used both
   * to partition the sections and to restore the mapping when an excluded
   * column is re-included. Columns absent from this map are "unmatched".
   */
  const [autoMappedFields, setAutoMappedFields] = useState<
    Record<string, string>
  >({})
  /**
   * Unmatched columns the user switched to "Map to existing" but has not yet
   * picked a target for (so `mapped` is still 'ignore' but the dropdown shows).
   */
  const [pendingMapColumns, setPendingMapColumns] = useState<Set<string>>(
    () => new Set()
  )

  // ---------------------------------------------------------------------------
  // Auto-match on mount (one-time sync — useMountEffect is the approved path)
  // ---------------------------------------------------------------------------
  useMountEffect(() => {
    if (!importData) return

    const allIgnored = importData.headers.every((h) => h.mapped === IGNORE)

    if (allIgnored) {
      const autoMapped = autoMatchWithConflictResolution(
        importData.headers,
        FILTERED_PATTERNS
      )
      const autoFields: Record<string, string> = {}
      autoMapped.forEach((h) => {
        if (h.mapped !== IGNORE) autoFields[h.original] = h.mapped
      })
      setAutoMappedFields(autoFields)
      const updatedData: ImportData = { ...importData, headers: autoMapped }
      dispatch({ type: 'SET_IMPORT_DATA', data: updatedData })
    } else {
      // Already mapped (e.g. back-nav). Treat currently-mapped columns as the
      // auto-matched section so the partition stays stable.
      const autoFields: Record<string, string> = {}
      importData.headers.forEach((h) => {
        if (h.mapped !== IGNORE) autoFields[h.original] = h.mapped
      })
      setAutoMappedFields(autoFields)
    }
  })

  // ---------------------------------------------------------------------------
  // Section partition (stable across include/skip toggles)
  // ---------------------------------------------------------------------------
  const headers = useMemo(
    () => importData?.headers ?? [],
    [importData?.headers]
  )

  const { autoColumns, unmatchedColumns } = useMemo(() => {
    const auto: typeof headers = []
    const unmatched: typeof headers = []
    headers.forEach((h) => {
      if (h.original in autoMappedFields) auto.push(h)
      else unmatched.push(h)
    })
    return { autoColumns: auto, unmatchedColumns: unmatched }
  }, [headers, autoMappedFields])

  // ---------------------------------------------------------------------------
  // Derived: effective (included) mapped set + canProceed gate
  // ---------------------------------------------------------------------------
  const mapped = useMemo(
    () => new Set((importData?.headers ?? []).map((h) => h.mapped)),
    [importData?.headers]
  )

  // At least a first name (or a full name) must be mapped. Last name is optional.
  const hasName =
    mapped.has('contact_full_name') || mapped.has('contact_first_name')
  const hasCompany = mapped.has('company_name')
  const hasEmail = (
    [
      'contact_work_email',
      'contact_personal_email',
      'contact_generic_email',
    ] as const
  ).some((k) => mapped.has(k))
  const canProceed = hasName && hasCompany && hasEmail

  const autoIncludedCount = autoColumns.filter(
    (h) => h.mapped !== IGNORE
  ).length

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  function updateMapping(originalHeader: string, newValue: string) {
    if (!importData) return
    const updated = setHeaderMapping(importData, originalHeader, newValue)
    dispatch({ type: 'SET_IMPORT_DATA', data: updated })
  }

  function toggleAutoInclude(originalHeader: string, included: boolean) {
    // Excluding sets mapped to 'ignore'; re-including restores the auto field.
    updateMapping(
      originalHeader,
      included ? IGNORE : (autoMappedFields[originalHeader] ?? IGNORE)
    )
  }

  function setUnmatchedAction(originalHeader: string, action: 'skip' | 'map') {
    setPendingMapColumns((prev) => {
      const next = new Set(prev)
      if (action === 'map') next.add(originalHeader)
      else next.delete(originalHeader)
      return next
    })
    if (action === 'skip') updateMapping(originalHeader, IGNORE)
  }

  function setUnmatchedTarget(originalHeader: string, fieldKey: string) {
    updateMapping(originalHeader, fieldKey === '' ? IGNORE : fieldKey)
  }

  async function handleNext() {
    if (!state.importData || !state.file) return

    const sig = mappingSignature(state.importData.headers)

    // Case 1 — Reuse: the mapping is unchanged since the last prepare, so the
    // existing preview's prepared data is still valid. Skip the backend
    // entirely and keep any edits the user made on the Validate step (those
    // live in state.importData).
    if (state.importNumber != null && sig === state.preparedMappingSig) {
      dispatch({ type: 'SET_STEP', step: 'validate' })
      return
    }

    setIsCreating(true)
    setCreateError(null)
    try {
      if (state.importNumber != null) {
        // Case 2 — Re-prepare: a preview already exists but the mapping
        // changed. Re-run prepare on the SAME import_number (no orphan).
        // Sending the current rows preserves user-column edits; prepare
        // recomputes the internal columns for the new mapping.
        await rePrepareImportPreview(
          state.importNumber,
          toRowsJSON(state.importData)
        )
        await pollUntilIdle(state.importNumber)
        const prepared = await fetchImportPreviewData(state.importNumber)
        dispatch({ type: 'SET_IMPORT_DATA', data: fromRowsJSON(prepared.data) })
        dispatch({ type: 'SET_PREPARED_MAPPING_SIG', sig })
        dispatch({ type: 'SET_STEP', step: 'validate' })
        return
      }

      // Case 3 — Create: first time through, no preview exists yet.
      const created = await createImportPreview({
        import_data: {
          data: toRowsJSON(state.importData),
          raw_data: await state.file.text(),
          name: state.file.name,
          skip_matching: true,
        },
      })
      // Poll until the Celery prepare_df task completes (adds ROW_ID /
      // COMPANY_HASH to rows) before fetching the prepared data. Without
      // this, a GET that lands before the task runs returns rows without
      // internal columns, breaking all downstream steps.
      await pollUntilIdle(created.import_number)
      const prepared = await fetchImportPreviewData(created.import_number)
      dispatch({
        type: 'SET_IMPORT_NUMBER',
        importNumber: created.import_number,
      })
      dispatch({ type: 'SET_IMPORT_DATA', data: fromRowsJSON(prepared.data) })
      dispatch({ type: 'SET_PREPARED_MAPPING_SIG', sig })
      dispatch({ type: 'SET_STEP', step: 'validate' })
    } catch {
      setCreateError('Failed to create import. Please try again.')
    } finally {
      setIsCreating(false)
    }
  }

  const rows = (importData?.rows ?? []) as Record<string, unknown>[]

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="absolute inset-0 flex flex-col">
      <div className="flex-1 overflow-y-auto min-h-0 px-8 py-6 flex flex-col gap-6">
        {/* Required-field gate banner */}
        {!canProceed && headers.length > 0 && (
          <div
            role="alert"
            className="flex items-start gap-3 px-4 py-3 rounded-lg bg-warning-bg border border-warning-border"
          >
            <AlertTriangle className="size-4 text-warning mt-0.5 shrink-0" />
            <p className="text-sm text-fg">
              <span className="font-semibold">Required:</span> Map a First Name
              (or Full Name), a Company Name, and at least one Email column to
              continue.
            </p>
          </div>
        )}

        {headers.length === 0 ? (
          <p className="text-sm text-fg-secondary">No CSV headers found.</p>
        ) : (
          <>
            {/* ── Auto-Matched Columns ─────────────────────────────────── */}
            {autoColumns.length > 0 && (
              <section>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-fg flex items-center gap-2">
                    <span className="inline-flex items-center justify-center size-5 rounded-full bg-success-bg text-success text-xs font-bold">
                      {autoColumns.length}
                    </span>
                    Auto-Matched Columns
                  </h3>
                  <div className="flex items-center gap-3">
                    {autoIncludedCount < autoColumns.length && (
                      <span className="text-xs text-fg-muted">
                        <span className="text-fg font-semibold">
                          {autoIncludedCount}
                        </span>
                        /{autoColumns.length} included
                      </span>
                    )}
                    <span className="text-xs text-fg-muted">
                      First Bite matched these automatically
                    </span>
                  </div>
                </div>

                <div className="rounded-xl border border-line overflow-hidden">
                  <div
                    className={cn(
                      'grid gap-3 px-4 py-2.5 bg-surface-sunken border-b border-line items-center',
                      AUTO_GRID
                    )}
                  >
                    <span className="text-xs font-semibold text-fg-muted uppercase tracking-wide">
                      CSV Column
                    </span>
                    <span className="text-xs font-semibold text-fg-muted uppercase tracking-wide">
                      First Bite Column
                    </span>
                    <span className="text-xs font-semibold text-fg-muted uppercase tracking-wide text-center">
                      Include
                    </span>
                    <span />
                  </div>

                  {autoColumns.map((header, idx) => {
                    const isIncluded = header.mapped !== IGNORE
                    const field = getColumnOption(
                      autoMappedFields[header.original]
                    )
                    return (
                      <div
                        key={header.original}
                        className={cn(
                          'grid gap-3 px-4 py-3 items-center transition-colors',
                          AUTO_GRID,
                          idx < autoColumns.length - 1 &&
                            'border-b border-line',
                          !isIncluded && 'bg-surface-sunken'
                        )}
                      >
                        <div className="min-w-0">
                          <span
                            className={cn(
                              'inline-block px-2 py-0.5 rounded text-xs font-mono truncate max-w-full bg-surface-sunken',
                              isIncluded ? 'text-fg-secondary' : 'text-fg-muted'
                            )}
                            title={header.original}
                          >
                            {header.original}
                          </span>
                        </div>

                        <span
                          className={cn(
                            'text-sm font-medium truncate',
                            isIncluded ? 'text-fg' : 'text-fg-muted'
                          )}
                        >
                          {field?.name}
                        </span>

                        <div className="flex items-center justify-center">
                          <Checkbox
                            checked={isIncluded}
                            onChange={() =>
                              toggleAutoInclude(header.original, isIncluded)
                            }
                            aria-label={`Include column "${header.original}"`}
                          />
                        </div>

                        <div className="flex items-center justify-center">
                          <SampleDataPreview
                            csvHeader={header.original}
                            mappedLabel={isIncluded ? field?.name : undefined}
                            isNotIncluded={!isIncluded}
                            rows={rows}
                            isActive={openPreview === header.original}
                            open={openPreview === header.original}
                            onOpenChange={(o) =>
                              setOpenPreview(o ? header.original : null)
                            }
                          />
                        </div>
                      </div>
                    )
                  })}
                </div>
              </section>
            )}

            {/* ── Unmatched Columns ────────────────────────────────────── */}
            {unmatchedColumns.length > 0 && (
              <section>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-fg flex items-center gap-2">
                    <span className="inline-flex items-center justify-center size-5 rounded-full bg-warning-bg text-warning-strong text-xs font-bold">
                      {unmatchedColumns.length}
                    </span>
                    Unmatched Columns
                  </h3>
                  <span className="text-xs text-fg-muted">
                    Choose what to do with each
                  </span>
                </div>

                <div className="rounded-xl border border-line overflow-hidden">
                  <div
                    className={cn(
                      'grid gap-3 px-4 py-2.5 bg-surface-sunken border-b border-line items-center',
                      UNMATCHED_GRID
                    )}
                  >
                    <span className="text-xs font-semibold text-fg-muted uppercase tracking-wide">
                      CSV Column
                    </span>
                    <span className="text-xs font-semibold text-fg-muted uppercase tracking-wide">
                      Action
                    </span>
                    <span className="text-xs font-semibold text-fg-muted uppercase tracking-wide">
                      Map To
                    </span>
                    <span />
                  </div>

                  {unmatchedColumns.map((header, idx) => {
                    const isMapped = header.mapped !== IGNORE
                    const isMapAction =
                      isMapped || pendingMapColumns.has(header.original)
                    const mappedField = isMapped
                      ? getColumnOption(header.mapped)
                      : undefined
                    return (
                      <div
                        key={header.original}
                        className={cn(
                          'grid gap-3 px-4 py-3 items-center transition-colors',
                          UNMATCHED_GRID,
                          idx < unmatchedColumns.length - 1 &&
                            'border-b border-line'
                        )}
                      >
                        <div className="min-w-0">
                          <span
                            className="inline-block px-2 py-0.5 rounded text-xs font-mono truncate max-w-full bg-warning-bg text-warning-strong"
                            title={header.original}
                          >
                            {header.original}
                          </span>
                        </div>

                        {/* Action dropdown */}
                        <div className="relative">
                          <select
                            value={isMapAction ? 'map' : 'skip'}
                            onChange={(e) =>
                              setUnmatchedAction(
                                header.original,
                                e.target.value as 'skip' | 'map'
                              )
                            }
                            aria-label={`Action for column "${header.original}"`}
                            className="w-full appearance-none border border-line rounded-lg pl-3 pr-8 py-2 text-sm text-fg bg-surface focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand cursor-pointer"
                          >
                            <option value="skip">Skip / Ignore</option>
                            <option value="map">Map to existing</option>
                            {/* TODO(FIR-7488): "Create custom column" action */}
                          </select>
                          <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 size-4 text-fg-muted pointer-events-none" />
                        </div>

                        {/* Target dropdown / skip hint */}
                        <div className="min-w-0">
                          {isMapAction ? (
                            <div className="relative">
                              <select
                                value={isMapped ? header.mapped : ''}
                                onChange={(e) =>
                                  setUnmatchedTarget(
                                    header.original,
                                    e.target.value
                                  )
                                }
                                aria-label={`Map column "${header.original}" to`}
                                className="w-full appearance-none border border-line rounded-lg pl-3 pr-8 py-2 text-sm text-fg bg-surface focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand cursor-pointer"
                              >
                                <option value="">Select a column…</option>
                                {TARGET_GROUPS.map((group) => (
                                  <optgroup
                                    key={group.label}
                                    label={group.label}
                                  >
                                    {group.keys.map((key) => (
                                      <option key={key} value={key}>
                                        {columnOptions[key].name}
                                      </option>
                                    ))}
                                  </optgroup>
                                ))}
                              </select>
                              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 size-4 text-fg-muted pointer-events-none" />
                            </div>
                          ) : (
                            <span className="text-sm text-fg-muted italic">
                              Will not be imported
                            </span>
                          )}
                        </div>

                        <div className="flex items-center justify-center">
                          <SampleDataPreview
                            csvHeader={header.original}
                            mappedLabel={mappedField?.name}
                            isNotIncluded={!isMapped}
                            rows={rows}
                            isActive={openPreview === header.original}
                            open={openPreview === header.original}
                            onOpenChange={(o) =>
                              setOpenPreview(o ? header.original : null)
                            }
                          />
                        </div>
                      </div>
                    )
                  })}
                </div>
              </section>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="px-8 py-4 border-t border-line bg-surface flex items-center justify-between shrink-0">
        <button
          type="button"
          className="px-4 py-2 text-sm font-medium rounded-lg border border-line text-fg hover:bg-surface-hover transition-colors"
          onClick={() => dispatch({ type: 'SET_STEP', step: 'upload' })}
        >
          Back
        </button>

        <div className="flex items-center gap-3">
          {createError !== null && (
            <p role="alert" className="text-sm text-error">
              {createError}
            </p>
          )}
          <button
            type="button"
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-brand text-white hover:bg-brand/90 transition-colors disabled:opacity-40"
            disabled={!canProceed || isCreating}
            onClick={handleNext}
          >
            {isCreating && <Loader2 className="size-4 animate-spin" />}
            {isCreating ? 'Creating import…' : 'Next: Validate'}
          </button>
        </div>
      </div>
    </div>
  )
}
